import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { API_BASE } from "../../lib/api";
import { toast } from "sonner";
import PlayerAvatar from "../../components/PlayerAvatar";
import Stars from "../../components/Stars";
import { ArrowLeft, FilePdf, Star, SoccerBall, Trophy, Medal, Shield, HandHeart, TrendUp } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const TABS = ["Overview", "Skills", "Attendance", "Progress"];

const AWARD_META = {
  player_of_month: { icon: Trophy, color: "#FFD166" },
  top_scorer: { icon: SoccerBall, color: "#D90429" },
  top_assister: { icon: HandHeart, color: "#FFD166" },
  best_attendance: { icon: Medal, color: "#06D6A0" },
  most_improved: { icon: TrendUp, color: "#FFD166" },
  best_defender: { icon: Shield, color: "#F5F5F5" },
  best_team_player: { icon: HandHeart, color: "#06D6A0" },
};

export default function ParentPlayerView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [skillHistory, setSkillHistory] = useState([]);
  const [tab, setTab] = useState("Overview");

  useEffect(() => {
    api.get(`/parent/players/${id}`).then((r) => setPlayer(r.data)).catch(() => navigate("/parent"));
    api.get(`/parent/players/${id}/attendance`).then((r) => setAttendance(r.data));
    api.get(`/parent/players/${id}/skills`).then((r) => setSkillHistory(r.data));
  }, [id, navigate]);

  function downloadReport() {
    const token = localStorage.getItem("wahdat_token");
    fetch(`${API_BASE}/reports/player/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => window.open(URL.createObjectURL(blob), "_blank"))
      .catch(() => toast.error("Could not download report"));
  }

  const progressData = React.useMemo(() => {
    return skillHistory.slice().reverse().map((h) => {
      const vals = Object.values(h.ratings || {}).filter((v) => v);
      return { date: h.date, overall: vals.length ? +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2) : 0 };
    });
  }, [skillHistory]);

  const latestSkills = skillHistory[0]?.ratings || {};

  if (!player) return <div className="overline">Loading…</div>;

  const skillAvg = player.skill_averages || {};
  const totals = player.match_totals || { goals: 0, assists: 0, minutes: 0, matches: 0 };

  return (
    <div className="space-y-6">
      <Link to="/parent" className="flex items-center gap-2 text-neutral-400 hover:text-white text-sm">
        <ArrowLeft size={16} weight="bold" /> My Children
      </Link>

      <div className="bg-[#121212] border border-neutral-800 rounded-lg overflow-hidden">
        <div className="h-32 md:h-40"
             style={{
               backgroundImage: "linear-gradient(180deg, rgba(5,5,5,0.5) 0%, rgba(5,5,5,0.9) 100%), linear-gradient(135deg, rgba(217,4,41,0.45), rgba(6,214,160,0.15))",
               backgroundSize: "cover", backgroundPosition: "center",
             }} />
        <div className="p-6 flex flex-col md:flex-row md:items-end gap-6 -mt-14">
          <PlayerAvatar player={player} size={112} className="border-4 border-[#121212] shadow-xl" />
          <div className="flex-1">
            <div className="flex items-baseline gap-3 flex-wrap">
              <h1 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tight">
                {player.first_name} {player.last_name}
              </h1>
              {player.jersey_number ? <span className="text-[#D90429] font-display font-black text-2xl">#{player.jersey_number}</span> : null}
            </div>
            <div className="flex gap-4 mt-2 text-sm text-neutral-400">
              <span className="overline">{player.position || "—"}</span>
              {player.age && <span className="overline">Age {player.age}</span>}
            </div>
          </div>
          <button
            onClick={downloadReport}
            data-testid="parent-download-report"
            className="h-12 px-5 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-display font-bold uppercase tracking-wider text-sm inline-flex items-center gap-2"
          >
            <FilePdf size={16} weight="bold" /> Download Report
          </button>
        </div>
        {player.awards?.length > 0 && (
          <div className="px-6 pb-5 pt-1 flex flex-wrap gap-2 border-t border-neutral-900" data-testid="parent-player-awards">
            {player.awards.map((a) => {
              const meta = AWARD_META[a.key] || { icon: Trophy, color: "#FFD166" };
              const Icon = meta.icon;
              return (
                <span
                  key={a.key}
                  data-testid={`parent-player-award-${a.key}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#0a0a0a] border border-neutral-800 text-xs font-bold uppercase tracking-wider"
                  style={{ color: meta.color }}
                >
                  <Icon size={14} weight="fill" /> {a.label}
                  {a.value != null && <span className="text-neutral-400 font-normal normal-case">· {a.value}</span>}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Season stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatBox label="Attendance" value={`${player.attendance_pct}%`} color="#06D6A0" />
        <StatBox label="Overall" value={`${player.overall_rating || 0} / 5`} color="#FFD166" />
        <StatBox label="Goals" value={totals.goals} color="#D90429" />
        <StatBox label="Assists" value={totals.assists} color="#FFD166" />
      </div>

      <div className="flex overflow-x-auto border-b border-neutral-800 -mx-4 px-4 sm:-mx-6 sm:px-6 gap-1">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`parent-tab-${t.toLowerCase()}`}
            className={`whitespace-nowrap px-4 py-3 text-sm font-bold uppercase tracking-wider border-b-2 transition-colors ${
              tab === t ? "border-[#D90429] text-white" : "border-transparent text-neutral-500 hover:text-neutral-300"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Overview" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CatBox title="Technical" value={skillAvg.technical} />
          <CatBox title="Physical" value={skillAvg.physical} />
          <CatBox title="Defending" value={skillAvg.defending} />
          <CatBox title="Mental" value={skillAvg.mental} />
          <CatBox title="Behavior" value={skillAvg.behavior} />
          <CatBox title="Minutes Played" value={totals.minutes} suffix="" isNumber />
        </div>
      )}

      {tab === "Skills" && (
        <div className="bg-[#121212] border border-neutral-800 rounded-lg p-6">
          <div className="overline mb-4">Latest Skill Ratings</div>
          {Object.keys(latestSkills).length === 0 ? (
            <div className="text-neutral-500 text-sm">Coach hasn't rated skills yet.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.entries(latestSkills).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between">
                  <div className="text-sm capitalize">{k.replace(/_/g, " ")}</div>
                  <Stars value={v} size={20} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "Attendance" && (
        <div className="bg-[#121212] border border-neutral-800 rounded-lg divide-y divide-neutral-900">
          {attendance.length === 0 ? (
            <div className="p-6 text-neutral-500 text-sm">No attendance records yet.</div>
          ) : attendance.map((a) => (
            <div key={a.id} className="p-4 flex items-center justify-between text-sm">
              <div>
                <div className="font-semibold">{a.date}</div>
                <div className="overline">{a.session_type}</div>
              </div>
              <StatusBadge status={a.status} />
            </div>
          ))}
        </div>
      )}

      {tab === "Progress" && (
        <div className="bg-[#121212] border border-neutral-800 rounded-lg p-4">
          <div className="overline mb-3">Overall Rating over Time</div>
          {progressData.length < 2 ? (
            <div className="text-neutral-500 text-sm p-6 text-center">Progress chart will appear as the coach adds more ratings.</div>
          ) : (
            <div style={{ height: 300 }}>
              <ResponsiveContainer>
                <LineChart data={progressData}>
                  <CartesianGrid stroke="#262626" strokeDasharray="3 3" />
                  <XAxis dataKey="date" stroke="#737373" fontSize={11} />
                  <YAxis domain={[0, 5]} stroke="#737373" fontSize={11} />
                  <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #262626" }} />
                  <Line type="monotone" dataKey="overall" stroke="#D90429" strokeWidth={2} dot={{ fill: "#D90429" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const StatBox = ({ label, value, color }) => (
  <div className="bg-[#121212] border border-neutral-800 rounded-lg p-4">
    <div className="overline">{label}</div>
    <div className="font-display font-black text-3xl mt-1" style={{ color }}>{value}</div>
  </div>
);

const CatBox = ({ title, value, suffix = "/5", isNumber = false }) => (
  <div className="bg-[#121212] border border-neutral-800 rounded-lg p-5">
    <div className="overline">{title}</div>
    <div className="font-display font-black text-3xl mt-2 text-[#F5F5F5]">
      {value || 0}{isNumber ? "" : suffix}
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    present: { bg: "#06D6A0", fg: "#050505", label: "Present" },
    absent: { bg: "#D90429", fg: "#fff", label: "Absent" },
    late: { bg: "#FFD166", fg: "#050505", label: "Late" },
    injured: { bg: "#F97316", fg: "#050505", label: "Injured" },
  };
  const c = map[status] || { bg: "#404040", fg: "#fff", label: status };
  return <span className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-md" style={{ background: c.bg, color: c.fg }}>{c.label}</span>;
};
